import { injectable } from "tsyringe";
import { TaskPriority } from "../domain/entities/AppStartUpEntity";
import { IAppStartUpRepository } from "../domain/repositories/IAppStartUpRepository";
import { AppStartUpModel } from "./models/AppStartUpModel";

@injectable()
export class AppStartUpImpl implements IAppStartUpRepository {
  private runningTasks = new Set<string>();
  private completedTasks = new Set<string>();

  async run(tasks: AppStartUpModel[], priority?: TaskPriority) {
    const filteredTasks = priority
      ? tasks.filter((task) => task.priority === priority)
      : tasks;

    for (const task of filteredTasks) {
      await this.runTask(task, tasks);
    }
  }

  async runByKeys(
    tasks: AppStartUpModel[],
    taskKeys: string[],
    priority?: TaskPriority,
  ) {
    const filteredTasks = (priority
      ? tasks.filter((task) => task.priority === priority)
      : tasks
    ).filter((task) => !!task?.key && taskKeys.includes(task.key));

    for (const task of filteredTasks) {
      await this.runTask(task, tasks);
    }
  }

  private async runTask(task: AppStartUpModel, allTasks: AppStartUpModel[]) {
    if (this.completedTasks.has(task.key)) return;
    if (this.runningTasks.has(task.key)) return;
    if (task.shouldSkip?.()) {
      this.completedTasks.add(task.key);
      return;
    }

    const dependencies = task.dependsOn || [];
    for (const depKey of dependencies) {
      const depTask = allTasks.find((item) => item.key === depKey);
      if (depTask && !this.completedTasks.has(depKey)) {
        await this.runTask(depTask, allTasks);
      }
    }

    this.runningTasks.add(task.key);

    try {
      await task.run();
      this.completedTasks.add(task.key);
    } catch (error) {
      console.error(`[StartupQueue] ${task.key} failed`, error);
    } finally {
      this.runningTasks.delete(task.key);
    }
  }

  reset() {
    this.runningTasks.clear();
    this.completedTasks.clear();
  }
}
